import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getLabel } from '../../../data/labels';
import MyBetsShareCard from '../MyBetsShareCard';
import { toPng } from 'html-to-image';
import '../MyBetsShareCard.css';
import { getConfiguration } from '../../utils/configuration';
import { xs_isMobile } from '../../..';
import { TicketPrinterContext } from '../../printer/TicketPrinterContext';
import SocialButtons from './SocialButtons';
import ShareModalV2 from './ShareModalV2';
import { getShareCardConfig } from '../MyBetsShareCardConfig';
import { isBetsson } from '../../utils/isBetsson';
import CheckBetSnapshotUI from '../my-bets-layout-2/checkbet-layout-2/CheckBetSnapshotUI';
import { systemCode } from '../../..';

const BANNER_DURATION = 5000;

function SavedBanner({ onDismiss }) {
    const [progress, setProgress] = useState(100);
    useEffect(() => {
        const startTime = Date.now();
        const interval = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const filled = Math.min(100, (elapsed / BANNER_DURATION) * 100);
            setProgress(filled);
            if (filled >= 100) { clearInterval(interval); onDismiss(); }
        }, 50);
        return () => clearInterval(interval);
    }, [onDismiss]);

    return (
        <div className="share-saved-banner">
            <div className="share-saved-banner__body">
                <i className="fa fa-check-circle share-saved-banner__icon" />
                <span>{getLabel('share.image_saved', 'Immagine salvata.')}</span>
                <button className="share-saved-banner__close" onClick={onDismiss}>✕</button>
            </div>
            <div className="share-saved-banner__bar">
                <div className="share-saved-banner__bar-fill" style={{ width: `${progress}%` }} />
            </div>
        </div>
    );
}


async function urlToBase64(url) {
    try {
        const res = await fetch(url);
        const blob = await res.blob();
        return await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch {
        return null;
    }
}

function waitForImages(container, timeoutMs = 5000) {
    const imgs = container.querySelectorAll('img');
    if (imgs.length === 0) return Promise.resolve();

    return Promise.race([
        Promise.all(
            Array.from(imgs).map(img => {
                if (img.complete && img.naturalWidth > 0) return Promise.resolve();
                return new Promise(resolve => {
                    img.onload = resolve;
                    img.onerror = resolve;
                });
            })
        ),
        new Promise(resolve => setTimeout(resolve, timeoutMs)),
    ]);
}

function ShareCardDownloader(props) {
    const conf = getConfiguration();
    const cardConfig = getShareCardConfig();
    const printerContext = useContext(TicketPrinterContext);
    const isDebug = props.debug === true;
    const ticketId = props.ticket?.id || props.ticketId;
    const ticketUrl = props.ticketUrl;
    const backgrounds = cardConfig.backgrounds || [];
    const defaultBgId = cardConfig.defaultBackgroundId || backgrounds[0]?.id || null;
    const refShareCard = useRef(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [downloadStatus, setDownloadStatus] = useState('idle');
    const [showBanner, setShowBanner] = useState(false);
    const [selectedBgId, setSelectedBgId] = useState(defaultBgId);
    const [hideStake, setHideStake] = useState(false);
    const [hideWinnings, setHideWinnings] = useState(false);
    const [copyLinkText, setCopyLinkText] = useState(null);
    const [resolvedBg, setResolvedBg] = useState(null);
    const [logoBase64, setLogoBase64] = useState(null);
    const [logoReady, setLogoReady] = useState(false);
    const [bgReady, setBgReady] = useState(false);
    const selectedBg = backgrounds.find(b => b.id === selectedBgId) || backgrounds[0] || null;
    const shareCardMounted = isDebug || modalOpen;
    const assetsReady = logoReady && bgReady;

    // Precarica logo come base64 quando si apre la modale
    useEffect(() => {
        if (!modalOpen) return;
        setLogoReady(false);
        const logoUrl = `/external_css/${systemCode}/img/share/logo.png`;
        urlToBase64(logoUrl).then(b64 => {
            if (b64) setLogoBase64(b64);
            setLogoReady(true);
        });
    }, [modalOpen]);

    useEffect(() => {
        if (!selectedBg) {
            setResolvedBg(null);
            setBgReady(true);
            return;
        }
        if (!selectedBg.imageUrl) {
            setResolvedBg(selectedBg);
            setBgReady(true);
            return;
        }

        setBgReady(false);
        urlToBase64(selectedBg.imageUrl).then(base64 => {
            setResolvedBg({ ...selectedBg, imageUrl: base64 || null });
            setBgReady(true);
        });
    }, [selectedBgId]); // eslint-disable-line react-hooks/exhaustive-deps


    const triggerErrorFeedback = () => {
        setDownloadStatus('error');
        setTimeout(() => setDownloadStatus('idle'), 3000);
    };

    const triggerSuccessFeedback = () => {
        setDownloadStatus('idle');
        setModalOpen(false);
        setShowBanner(true);
    };

    function triggerCopyFeedback() {
        setCopyLinkText(getLabel('checkbet.link_copied'));
        setTimeout(() => setCopyLinkText(null), 1500);
    }

    function downloadDirect(dataUrl, fileName) {
        const link = document.createElement('a');
        link.download = fileName;
        link.href = dataUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        triggerSuccessFeedback();
    }


    function handleShareLink() {
        if (isBetsson() && navigator.share && xs_isMobile) {
            navigator.share({
                url: ticketUrl,
                title: `${conf.sys} | ${getLabel('betslip.reservation.share')}`,
                text: getLabel('betslip.reservation.share_bet'),
            }).catch(() => { });
        } else {
            const android = window.android;
            if (android && typeof android.writeTextClipboard === 'function') {
                android.writeTextClipboard(ticketUrl);
                triggerCopyFeedback();
            } else {
                navigator.clipboard.writeText(ticketUrl).then(triggerCopyFeedback);
            }
        }
    }

    function handlePrint() {
        printerContext.printTicket(ticketId, false);
        setModalOpen(false);
    }

    const handleDownloadImage = useCallback(async () => {
        if (!ticketId) return;
        if (!refShareCard.current) { triggerErrorFeedback(); return; }

        setDownloadStatus('loading');

        try {
            const node = refShareCard.current;
            await waitForImages(node);

            await new Promise(resolve => requestAnimationFrame(() => {
                requestAnimationFrame(resolve);
            }));

            const width = 600;
            const height = node.scrollHeight;

            const toPngOptions = {
                cacheBust: true,
                skipFonts: true,
                filter: (n) => {
                    if (n.tagName === 'IFRAME') return false;
                    return true;
                },
                backgroundColor: resolvedBg?.color || cardConfig.backgroundColor,
                pixelRatio: 2,
                useCORS: true,
                allowTaint: true,
                width,
                height,
                style: {
                    transform: 'scale(1)',
                    transformOrigin: 'top left',
                    width: `${width}px`,
                    height: `${height}px`,
                    maxHeight: 'none',
                },
            };

            // Tripla chiamata, bug noto html-to-image, le prime due warmup la cache
            await toPng(node, toPngOptions);
            await toPng(node, toPngOptions);
            const dataUrl = await toPng(node, toPngOptions);

            const android = window.android;
            const fileName = `Ticket_${ticketId}.png`;
            const fileType = 'image/png';
            const isIOSApp = document.body.classList.contains('ios-wrapper');
            const isAndroidApp = document.body.classList.contains('android-wrapper');

            // Helper per convertire dataUrl in File (riusato in più rami)
            const dataUrlToFile = () => {
                const parts = dataUrl.split(',');
                const mime = parts[0].match(/:(.*?);/)[1];
                const binary = window.atob(parts[1]);
                const arr = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i++) arr[i] = binary.charCodeAt(i);
                const blob = new Blob([arr], { type: mime });
                return new File([blob], fileName, { type: fileType });
            };

            if (isIOSApp && navigator.canShare) {
                const file = dataUrlToFile();
                if (navigator.canShare({ files: [file] })) {
                    try {
                        await navigator.share({ files: [file] });
                        triggerSuccessFeedback();
                    } catch (err) {
                        if (err.name === 'AbortError') setDownloadStatus('idle');
                        else downloadDirect(dataUrl, fileName);
                    }
                    return;
                }
            }

            if (isAndroidApp && android && typeof android.shareFiles === 'function') {
                const pureBase64 = dataUrl.split(',')[1];
                android.shareFiles(JSON.stringify({
                    files: [{ name: fileName, type: fileType, dataUrl: pureBase64 }],
                }));
                triggerSuccessFeedback();
                return;
            }

            if (xs_isMobile && navigator.canShare) {
                const file = dataUrlToFile();
                if (navigator.canShare({ files: [file] })) {
                    try {
                        await navigator.share({ files: [file] });
                        triggerSuccessFeedback();
                    } catch (err) {
                        if (err.name === 'AbortError') setDownloadStatus('idle');
                        else downloadDirect(dataUrl, fileName);
                    }
                    return;
                }
            }

            downloadDirect(dataUrl, fileName);

        } catch {
            triggerErrorFeedback();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketId, ticketUrl, resolvedBg, hideStake, hideWinnings]);

    function handleShareDirect() {
        if (isBetsson() && navigator.share && xs_isMobile) {
            navigator.share({
                url: ticketUrl,
                title: `${conf.sys} | ${getLabel('betslip.reservation.share')}`,
                text: getLabel('betslip.reservation.share_bet'),
            }).catch(() => { });
        } else if (props.onGoShareLinkView) {
            props.onGoShareLinkView();
        } else {
            handleShareLink();
        }
    }

    const shareCardNode = ticketId ? (
        <MyBetsShareCard
            ref={refShareCard}
            ticketId={ticketId}
            isSnapshot={true}
            winPot={props.winPot}
            totalOdd={props.totalOdd}
            totalAmount={props.totalAmount}
            bonusAmount={props.bonusAmount}
            ticketTypeLabel={props.ticketTypeLabel}
            dateTimestamp={props.dateTimestamp}
            status={props.status}
            hideStake={hideStake}
            hideWinnings={hideWinnings}
            selectedBackground={resolvedBg}
            logoBase64={logoBase64}
            SnapshotComponent={CheckBetSnapshotUI}
        />
    ) : (
        <div className="share-capture__error">
            {getLabel('control_panel.print_tickets.search_results.not_ticket_found')}
        </div>
    );

    return (
        <>
            {showBanner && <SavedBanner onDismiss={() => setShowBanner(false)} />}

            <div
                id="checkbet-share-buttons-container"
                className={`mb_7_1_2${conf.sdt ? ' sdt-enabled' : ''}`}
            >
                {!conf.sdt && (
                    <SocialButtons
                        message={getLabel('checkbet.share_ticket')}
                        ticketUrl={ticketUrl}
                    />
                )}
                <div
                    className="checkbet-button mr-auto | mb_7_1_2_2"
                    onClick={conf.sdt ? () => setModalOpen(true) : handleShareDirect}
                >
                    {copyLinkText
                        ? copyLinkText
                        : <>{conf.sdt && <i className="fa fa-share ml-2"></i>} {getLabel('betslip.reservation.share')}</>
                    }
                </div>
            </div>

            {conf.sdt && modalOpen && (
                <ShareModalV2
                    onClose={() => setModalOpen(false)}
                    onShareLink={handleShareLink}
                    onPrint={handlePrint}
                    onSaveImage={handleDownloadImage}
                    copyLinkText={copyLinkText}
                    downloadStatus={downloadStatus}
                    backgrounds={backgrounds}
                    selectedBgId={selectedBgId}
                    onSelectBg={setSelectedBgId}
                    hideStake={hideStake}
                    onToggleStake={() => setHideStake(v => !v)}
                    hideWinnings={hideWinnings}
                    onToggleWinnings={() => setHideWinnings(v => !v)}
                    assetsReady={assetsReady}
                />
            )}

            {conf.sdt && shareCardMounted && (
                <div className={isDebug ? 'share-capture-wrapper--debug' : 'share-capture-wrapper--hidden'}>
                    {shareCardNode}
                </div>
            )}
        </>
    );
}

export default ShareCardDownloader;